/**
 * CitationPill Component
 *
 * Displays an inline citation as a light-grey pill showing the first 2 words
 * of the source text. On hover (web) or press (mobile), shows the full source text.
 */

import React, { useState } from 'react';
import { View, Text, StyleSheet, Platform, Pressable, Modal, TouchableOpacity, LayoutChangeEvent } from 'react-native';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interSemiBold, interMedium, interRegular } from "../../lib/fonts";
import LaTeXText from '../LaTeXText';
import { Trans } from '@lingui/react/macro';

interface PillPosition {
    y: number;
    height: number;
}

interface CitationPillProps {
    chunkText: string;
    pillNumber: number;
    onHoverChange?: (isHovered: boolean, tooltipText?: string, position?: PillPosition) => void;
}

/**
 * Clean chunk text by normalizing whitespace and removing line breaks
 */
function cleanChunkText(text: string): string {
    return text
        .replace(/-(\r\n|\n|\r)\s*/g, '')  // Remove hyphenation at line breaks
        .replace(/\s+/g, ' ')              // Normalize whitespace
        .replace(/(\r\n|\n|\r)/g, ' ')     // Remove line breaks
        .trim();
}

export default function CitationPill({ chunkText, pillNumber, onHoverChange }: CitationPillProps) {
    const { fontsLoaded } = useFontLoading();
    const [showTooltip, setShowTooltip] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [layout, setLayout] = useState<PillPosition | null>(null);

    // Clean the chunk text for better display
    const cleanedText = cleanChunkText(chunkText);
    const pillLabel = pillNumber.toString();

    // Track pill position for tooltip placement
    const handleLayout = (event: LayoutChangeEvent) => {
        const { y, height } = event.nativeEvent.layout;
        setLayout({ y, height });
    };

    // Notify parent of hover state changes
    const handleHoverIn = () => {
        setIsHovered(true);
        if (onHoverChange) {
            onHoverChange(true, cleanedText, layout || undefined);
        }
    };

    const handleHoverOut = () => {
        setIsHovered(false);
        if (onHoverChange) {
            onHoverChange(false);
        }
    };

    // For mobile: use modal tooltip
    const tooltipModal = (
        <Modal
            visible={showTooltip}
            transparent
            animationType="fade"
            onRequestClose={() => setShowTooltip(false)}
        >
            <Pressable style={styles.modalOverlay} onPress={() => setShowTooltip(false)}>
                <View style={styles.tooltipModal}>
                    <LaTeXText
                        text={cleanedText}
                        style={[styles.modalTooltipText, fontsLoaded && interRegular]}
                        fontsLoaded={fontsLoaded}
                    />
                    <TouchableOpacity
                        style={styles.closeButton}
                        onPress={() => setShowTooltip(false)}
                    >
                        <Text style={[styles.closeButtonText, fontsLoaded && interSemiBold]}><Trans id="papers.citationpill.close" comment="UI copy in components/papers/CitationPill.tsx">
                            Close
                        </Trans></Text>
                    </TouchableOpacity>
                </View>
            </Pressable>
        </Modal>
    );

    if (Platform.OS === 'web') {
        // Web: just render pill, tooltip is handled at section level
        return (
            <View
                style={[styles.pillContainer, isHovered && styles.pillContainerHovered]}
                onLayout={handleLayout}
            >
                <Pressable
                    style={[styles.pill, isHovered && styles.pillHovered]}
                    onHoverIn={handleHoverIn}
                    onHoverOut={handleHoverOut}
                >
                    <Text style={[styles.pillText, fontsLoaded && interRegular]}>
                        {pillLabel}
                    </Text>
                </Pressable>
            </View>
        );
    }

    // Mobile: use press to show modal
    return (
        <>
            <Pressable
                style={styles.pill}
                onPress={() => setShowTooltip(true)}
            >
                <Text style={[styles.pillText, fontsLoaded && interRegular]}>
                    {pillLabel}
                </Text>
            </Pressable>
            {tooltipModal}
        </>
    );
}

const styles = StyleSheet.create({
    pillContainer: {
        position: 'relative',
    },
    pillContainerHovered: {
        zIndex: 10, // Elevate above sibling pills in same section
    },
    pill: {
        backgroundColor: '#e5e7eb', // light grey
        borderRadius: 10,
        paddingVertical: 1,
        paddingHorizontal: 14,
        marginHorizontal: 4,
        alignSelf: 'flex-start',
        minWidth: 32,
        alignItems: 'center',
        justifyContent: 'center',
        height: 18,
    },
    pillHovered: {
        backgroundColor: '#d1d5db', // slightly darker on hover
    },
    pillText: {
        fontSize: 12,
        color: '#6b7280', // normal grey
        fontWeight: '500',
        textAlign: 'center',
        lineHeight: 14,
        includeFontPadding: false, // Android: remove extra padding
    },
    // Mobile modal
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    tooltipModal: {
        backgroundColor: '#ffffff',
        borderRadius: 12,
        padding: 20,
        maxWidth: 400,
        width: '100%',
        ...(Platform.OS === 'ios' && {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
        }),
        ...(Platform.OS === 'android' && {
            elevation: 5,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.25)',
        }),
    },
    modalTooltipText: {
        fontSize: 14,
        color: colors.text900,
        lineHeight: 20,
    },
    closeButton: {
        marginTop: 16,
        backgroundColor: colors.primaryGreen,
        borderRadius: 8,
        padding: 12,
        alignItems: 'center',
    },
    closeButtonText: {
        fontSize: 16,
        color: '#ffffff',
        fontWeight: '600',
    },
});
